import { GamePlayScene } from '../scenes/GamePlayScene';
import { BasicAlien } from '../entities/monsters/BasicAlien';
import { FastAlien } from '../entities/monsters/FastAlien';
import { TankAlien } from '../entities/monsters/TankAlien';
import { ShieldedAlien } from '../entities/monsters/ShieldedAlien';
import { SplittingAlien } from '../entities/monsters/SplittingAlien';
import { BasicBoss } from '../entities/bosses/BasicBoss';
import { ShieldBoss } from '../entities/bosses/ShieldBoss';
import { SummonerBoss } from '../entities/bosses/SummonerBoss';
import { UltimateBoss } from '../entities/bosses/UltimateBoss';
import { Monster } from '../entities/monsters/Monster';
import { Boss } from '../entities/bosses/Boss';

interface DifficultySettings {
  monsterHealthMultiplier: number;
  monsterSpeedMultiplier: number;
  spawnDelayMs: number;
  maxMonstersOnScreen: number;
  bossChance: number;
  availableMonsterTypes: string[];
}

export class MonsterSpawnManager {
  private scene: GamePlayScene;
  private spawnTimer: Phaser.Time.TimerEvent | null = null;
  private isSpawning: boolean = false;
  private currentLevel: number = 1;
  
  // Speed multiplier for fast gameplay
  private gameSpeedMultiplier: number = 2.0; // 2x faster game speed

  constructor(scene: GamePlayScene) {
    this.scene = scene;
  }

  startSpawning(level: number): void {
    this.currentLevel = level;
    this.isSpawning = true;
    
    const difficultySettings = this.calculateDifficultySettings(level);
    
    // Apply speed multiplier to spawn delay
    const adjustedSpawnDelay = Math.max(200, difficultySettings.spawnDelayMs / this.gameSpeedMultiplier);
    
    this.spawnTimer = this.scene.time.addEvent({
      delay: adjustedSpawnDelay,
      callback: () => this.spawnMonstersBalanced(difficultySettings),
      loop: true
    });
  }

  private calculateDifficultySettings(level: number): DifficultySettings {
    // Progressive difficulty with faster progression
    const healthBase = 0.5 + (level * 0.15); // Faster health scaling
    const speedBase = 0.2 + (level * 0.02); // Faster speed scaling
    
    // Much faster spawn rates
    const baseSpawnDelay = Math.max(100, 1200 - (level * 40)); // Minimum 100ms, decreases faster
    
    // More aggressive monster limits
    const maxMonsters = 300; // Up to 300 monsters
    
    // Higher boss chance progression
    const bossChance = Math.min(0.5, (level - 1) * 0.05); // Up to 50% boss chance
    
    // Faster monster type unlocking
    let availableTypes = ['basic'];
    if (level >= 1) availableTypes.push('fast'); // Immediately available
    if (level >= 2) availableTypes.push('tank');
    if (level >= 3) availableTypes.push('shielded');
    if (level >= 4) availableTypes.push('splitting');
    
    return {
      monsterHealthMultiplier: healthBase,
      monsterSpeedMultiplier: speedBase * this.gameSpeedMultiplier, // Apply speed multiplier
      spawnDelayMs: baseSpawnDelay,
      maxMonstersOnScreen: maxMonsters,
      bossChance: bossChance,
      availableMonsterTypes: availableTypes
    };
  }

  private spawnMonstersBalanced(settings: DifficultySettings): void {
    if (!this.isSpawning) return;

    const currentMonsters = this.getCurrentMonsterCount();
    // More aggressive spawning
    const spawnThreshold = Math.max(5, settings.maxMonstersOnScreen * 0.6);
    
    if (currentMonsters < spawnThreshold) {
      // Spawn multiple monsters for faster action
      const spawnCount = Math.floor(Math.random() * 20) + 5;
      
      for (let i = 0; i < spawnCount; i++) {
        // Boss spawn check
        if (Math.random() < settings.bossChance) {
          this.spawnBoss(settings);
        } else {
          this.spawnRandomMonster(settings);
        }
      }
    }
  }

  private spawnRandomMonster(settings: DifficultySettings): void {
    const monsterType = settings.availableMonsterTypes[
      Math.floor(Math.random() * settings.availableMonsterTypes.length)
    ];
    
    const spawnPosition = this.getRandomSpawnPosition();
    let monster: Monster;
    
    switch (monsterType) {
      case 'fast':
        monster = new FastAlien(this.scene, spawnPosition.x, spawnPosition.y);
        break;
      case 'tank':
        monster = new TankAlien(this.scene, spawnPosition.x, spawnPosition.y);
        break;
      case 'shielded':
        monster = new ShieldedAlien(this.scene, spawnPosition.x, spawnPosition.y);
        break;
      case 'splitting':
        monster = new SplittingAlien(this.scene, spawnPosition.x, spawnPosition.y);
        break;
      default:
        monster = new BasicAlien(this.scene, spawnPosition.x, spawnPosition.y);
    }
    
    // Apply difficulty scaling with speed multiplier
    monster.setRateSpeed(settings.monsterSpeedMultiplier);
    
    this.scene.add.existing(monster);
    
    // Emit spawn event for managers
    this.scene.events.emit('monsterSpawned', {
      monsters: [monster],
      boss: null
    });
  }

  private spawnBoss(settings: DifficultySettings): void {
    const bossTypes = ['basic', 'shield', 'summoner'];
    
    // Higher level bosses unlock faster
    if (this.currentLevel >= 8) bossTypes.push('ultimate');
    
    const bossType = bossTypes[Math.floor(Math.random() * bossTypes.length)];
    const spawnPosition = this.getRandomSpawnPosition();
    
    let boss: Boss;
    
    switch (bossType) {
      case 'shield':
        boss = new ShieldBoss(this.scene, spawnPosition.x, spawnPosition.y);
        break;
      case 'summoner':
        boss = new SummonerBoss(this.scene, spawnPosition.x, spawnPosition.y);
        break;
      case 'ultimate':
        boss = new UltimateBoss(this.scene, spawnPosition.x, spawnPosition.y);
        break;
      default:
        boss = new BasicBoss(this.scene, spawnPosition.x, spawnPosition.y);
    }
    
    // Apply difficulty scaling with enhanced stats for faster gameplay
    boss.setDifficulty(1.5); // Bosses get extra health
    
    this.scene.add.existing(boss);
    
    // Emit boss spawn event
    this.scene.events.emit('monsterSpawned', {
      monsters: [],
      boss: boss
    });
  }

  private getRandomSpawnPosition(): { x: number, y: number } {
    const width = this.scene.cameras.main.width;
    const height = this.scene.cameras.main.height;
    
    // Spawn from all edges for more action
    const edge = Math.floor(Math.random() * 4);
    
    switch (edge) {
      case 0: // Top
        return { x: Math.random() * width, y: -50 };
      case 1: // Right
        return { x: width + 50, y: Math.random() * height };
      case 2: // Bottom
        return { x: Math.random() * width, y: height + 50 };
      case 3: // Left
        return { x: -50, y: Math.random() * height };
      default:
        return { x: -50, y: Math.random() * height };
    }
  }

  private getCurrentMonsterCount(): number {
    // Count monsters in scene
    let count = 0;
    this.scene.children.list.forEach(child => {
      if (child instanceof Monster || child instanceof Boss) {
        count++;
      }
    });
    return count;
  }

  // Public method to increase game speed further
  setGameSpeed(multiplier: number): void {
    this.gameSpeedMultiplier = multiplier;
    
    // Update current spawn timer if active
    if (this.spawnTimer && this.isSpawning) {
      this.spawnTimer.destroy();
      this.startSpawning(this.currentLevel);
    }
  }

  getGameSpeed(): number {
    return this.gameSpeedMultiplier;
  }

  stopSpawning(): void {
    this.isSpawning = false;
    if (this.spawnTimer) {
      this.spawnTimer.destroy();
      this.spawnTimer = null;
    }
  }

  updateDifficultySettings(level: number): void {
    this.currentLevel = level;
    if (this.isSpawning) {
      this.stopSpawning();
      this.startSpawning(level);
    }
  }
} 